import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ownerKey, useOwnerId } from "../features/auth/owner.ts";
import { api, query, type Schemas } from "./client.ts";

/**
 * La campana. Los avisos no son filas: se DERIVAN del estado en cada peticion
 * —abiertas sin cerrar, cerradas sin revisar, dias sin registrar, reglas
 * innegociables rotas y planes sin ejecutar—, asi que aqui no hay ni un hook
 * para crear un aviso ni uno para borrarlo.
 *
 * Lo que se escribe son dos marcas y ningun aviso: VISTO, que apaga el contador
 * y deja la tarjeta, y DESCARTADO, que la quita de la lista hasta que haya un
 * hecho mas reciente. Un aviso desaparece de verdad cuando su condicion deja de
 * cumplirse, y eso pasa al revisar la operacion, no al llamar aqui.
 *
 * NO SE CONSULTA EN BUCLE. Un temporizador cada minuto son sesenta peticiones a
 * la hora por pestana abierta para enterarse de algo que casi siempre cambia por
 * lo que el propio usuario acaba de hacer. Se relee al abrir el panel y cuando
 * una operacion cambia, que son los dos momentos en los que la respuesta puede
 * ser distinta.
 */

export type Notifications = Schemas["NotificationsResponse"];
export type Notification = Schemas["NotificationDto"];
export type NotificationKind = Schemas["NotificationKind"];

/* Es el PREFIJO: las mutaciones invalidan por el, y la clave completa lleva
   el dueno detras (ver `api/hooks.ts`). Los avisos de otro son otra cache. */
export const NOTIFICATIONS_KEY = ["notifications"] as const;

export function useNotifications() {
  const owner = useOwnerId();

  return useQuery({
    queryKey: [...NOTIFICATIONS_KEY, ownerKey(owner)] as const,
    queryFn: () => api.get<Notifications>(`/api/v1/notifications${query({ owner })}`),
    staleTime: 60_000,
  });
}

/**
 * Marcar como vistos. Sin `kind`, todos los que hay ahora mismo; con `kind`,
 * solo ese.
 *
 * El `kind` viaja como CADENA y no como enum, porque asi lo declara el contrato:
 * Minimal APIs enlaza un enum sin `ignoreCase` ni comprobar que el valor exista,
 * y un nombre mal escrito salia como 500 en vez de como 400 (SPEC 19.8).
 */
export function useMarkNotificationsSeen() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (kind: NotificationKind | null) =>
      api.post<void>("/api/v1/notifications/seen", { kind }),
    onSuccess: () => client.invalidateQueries({ queryKey: NOTIFICATIONS_KEY }),
  });
}

/**
 * QUITAR un aviso de la lista. Sin `kind`, todos los que hay ahora; con `kind`,
 * solo ese, que es la X de cada tarjeta.
 *
 * DESCARTAR NO ES BORRAR, y el nombre del hook lo dice a proposito. El servidor
 * guarda "de este aviso, lo de hasta aqui ya no quiero verlo": en cuanto aparece
 * un hecho mas reciente -- otra operacion sin revisar, otra regla rota -- el
 * aviso vuelve entero. Borrarlo de verdad haria que el dia que el operador rompa
 * una regla innegociable el sistema se lo callara, y entonces el diario deja de
 * servir para lo unico que sirve (0024_avisos_descartados.sql).
 */
export function useDismissNotifications() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (kind: NotificationKind | null) =>
      api.post<void>("/api/v1/notifications/dismiss", { kind }),
    onSuccess: () => client.invalidateQueries({ queryKey: NOTIFICATIONS_KEY }),
  });
}
